import React, { useState, useRef, useEffect, useCallback } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  Building2,
  User,
  MapPin,
  FileText,
  Upload,
  X,
  RefreshCw,
  FilePlus,
  Sparkles,
} from 'lucide-react'
import { HubShell, StepTabs, ConfirmationToggle, type HubStep } from './HubShell'
import { EsignDocumentsPanel, type EsignDocumentItem, type EsignParty } from './EsignDocumentsPanel'
import { DocumentSigningModal } from './DocumentSigningModal'
import { DocumentReadinessPanel } from '../../services/esign/DocumentReadinessPanel'
import { evaluateMultiple } from '../../services/esign/useDocumentReadiness'
import { PURCHASE_HUB_DOCUMENTS } from '../../config/hubDocuments'
import { Label } from '../ui/Label'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import toast from 'react-hot-toast'
import { useHubData, type HubData } from '../../hooks/useHubData'
import { useHubAccess } from '../../hooks/useHubAccess'
import { useDemoAcquisitionPrefill } from '../../hooks/useDemoAcquisitionPrefill'
import { useAuth } from '../../contexts/AuthContext'
import { useExchange } from '../../contexts/ExchangeContext'
import { activityLog } from '../../services/activityLog'
import { documentExtraction } from '../../services/documentExtraction'
import { wizardApi } from '../../services/api/wizard'
import type { DocumentRenderData } from '../../services/esign/types'
import {
  validatePurchaseClose,
  type IdentificationRule,
} from '../../lib/identification/ruleEnforcement'
import type { IdentifiedProperty } from '../identification/IdentificationPropertyRow'
import {
  PurchaseFinalizationList,
  buildInitialFinalizationStates,
  finalizationReadyToConfirm,
  type PropertyFinalizationState,
} from './PurchaseFinalizationList'
import { PurchaseAgreementExtracting } from './PurchaseAgreementExtracting'
import { PurchaseAgreementReveal, type PurchaseAgreementExtracted } from './PurchaseAgreementReveal'
import { MaskedSsnField } from './MaskedSsnField'
import { ReadOnlyField, EditableOrReadOnlyField } from './EditableOrReadOnlyField'
import { LoadingSpinner } from '../ui/LoadingSpinner'

type DocumentType = 'assignment-of-purchase-contract' | 'notice-of-assignment-seller'

const DOC_TYPE_MAP: Record<string, DocumentType> = {
  'Assignment of Replacement Property Contract': 'assignment-of-purchase-contract',
  'Notice of Assignment (Seller)': 'notice-of-assignment-seller',
}

type PurchaseStep =
  | 'exchanger'
  | 'purchase-agreement'
  | 'seller'
  | 'closing-company'
  | 'property'
  | 'documents'

type ManualSellerFields = {
  sellerName: string
  sellerAcknowledgmentSigner: string
}

type ManualClosingFields = {
  closingCompanyName: string
  closingCompanyAddress: string
  closingCompanyPhone: string
  closingCompanyEmail: string
  closerName: string
  closerEmail: string
}

type ManualPropertyFields = {
  purchaseAgreementDate: string
  closingDate: string
}

type PurchaseAgreementPhase = 'idle' | 'uploading' | 'extracting' | 'revealed'

type PurchaseDocState = {
  uploaded: boolean
  signed: boolean
  signedAt?: string
  signedBy?: string
  signature?: string
  uploadedAt?: string
  fileName?: string
}

const HUB_ID = 'purchase'

const TAB_CONFIRM_KEYS: Record<string, string> = {
  exchanger: 'exchangerConfirmed',
  'purchase-agreement': 'purchaseAgreementConfirmed',
  seller: 'sellerConfirmed',
  'closing-company': 'closingCompanyConfirmed',
  property: 'propertyConfirmed',
  documents: 'documentsConfirmed',
}

const TAB_CONFIRM_LABELS: Record<string, string> = {
  exchanger: 'I confirm the exchanger details are accurate.',
  'purchase-agreement': 'I confirm the extracted Purchase Agreement data and am ready to proceed.',
  seller: 'I confirm the seller details are accurate.',
  'closing-company': 'I confirm the closing-company details are accurate.',
  property: 'I confirm the replacement-property details are accurate.',
  documents: 'I confirm all documents are complete and accurate, and I\'m ready to submit.',
}

function tabConfirmStorageKey(userId: string, tab: string): string {
  return `${HUB_ID}_tab_${tab}_confirmed_${userId}`
}

function buildInitialDocStatuses(): Record<string, PurchaseDocState> {
  const statuses: Record<string, PurchaseDocState> = {}
  PURCHASE_HUB_DOCUMENTS.forEach((doc) => {
    statuses[doc.name] = { uploaded: false, signed: false }
  })
  return statuses
}


const exchangerEditSchema = z.object({
  homeAddress: z.string().min(1, 'Address is required'),
  taxIdNumber: z.string().min(1, 'Tax ID is required'),
})

type ExchangerEditValues = z.infer<typeof exchangerEditSchema>

const sellerEditSchema = z.object({
  sellerName: z.string().min(1, 'Seller name is required'),
  sellerAcknowledgmentSigner: z.string(),
})

type SellerEditValues = z.infer<typeof sellerEditSchema>

const closingEditSchema = z.object({
  closingCompanyName: z.string().min(1, 'Company name is required'),
  closingCompanyAddress: z.string().min(1, 'Address is required'),
  closingCompanyPhone: z.string(),
  closingCompanyEmail: z.string(),
  closerName: z.string(),
  closerEmail: z.string(),
})

type ClosingEditValues = z.infer<typeof closingEditSchema>

const propertyEditSchema = z.object({
  purchaseAgreementDate: z.string(),
  closingDate: z.string(),
})

type PropertyEditValues = z.infer<typeof propertyEditSchema>

interface ExchangerTabProps {
  names: string
  homeAddress: string
  taxIdNumber: string
  disabled: boolean
  onFieldsSaved: (values: Partial<ExchangerEditValues>) => void
}

const ExchangerTab: React.FC<ExchangerTabProps> = ({
  names,
  homeAddress,
  taxIdNumber,
  disabled,
  onFieldsSaved,
}) => {
  const needsAddress = !homeAddress
  const needsTaxId = !taxIdNumber

  const { register, handleSubmit, formState: { errors } } = useForm<ExchangerEditValues>({
    resolver: zodResolver(exchangerEditSchema),
    defaultValues: {
      homeAddress: homeAddress || '',
      taxIdNumber: taxIdNumber || '',
    },
  })

  const onSave = useCallback(
    (values: ExchangerEditValues) => {
      const patch: Partial<ExchangerEditValues> = {}
      if (needsAddress) patch.homeAddress = values.homeAddress
      if (needsTaxId) patch.taxIdNumber = values.taxIdNumber
      onFieldsSaved(patch)
    },
    [needsAddress, needsTaxId, onFieldsSaved]
  )

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10 px-4 py-3">
        <div className="flex items-start gap-3">
          <User
            className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          <div>
            <p className="text-sm font-semibold text-purple-900 dark:text-purple-100">
              Exchanger Information
            </p>
            <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
              Verify the exchanger details for the replacement property closing.
            </p>
          </div>
        </div>
      </div>
      <form
        id="exchanger-editable-form"
        onSubmit={handleSubmit(onSave)}
        noValidate
        className="space-y-3"
      >
        <ReadOnlyField label="Exchanger Name(s)" value={names} />
        {needsAddress ? (
          <div className="space-y-1">
            <Label
              htmlFor="exchanger-home-address"
              className="text-xs text-purple-700 dark:text-purple-300"
            >
              Home / Mailing Address
            </Label>
            <Input
              id="exchanger-home-address"
              aria-label="Home / Mailing Address"
              placeholder="Enter mailing address"
              disabled={disabled}
              {...register('homeAddress')}
              className="border-purple-300 dark:border-purple-600 focus-visible:ring-purple-400"
            />
            {errors.homeAddress && (
              <p className="text-xs text-red-600 dark:text-red-400" role="alert">
                {errors.homeAddress.message}
              </p>
            )}
          </div>
        ) : (
          <ReadOnlyField label="Home / Mailing Address" value={homeAddress} />
        )}
        {needsTaxId ? (
          <div className="space-y-1">
            <Label
              htmlFor="exchanger-tax-id"
              className="text-xs text-purple-700 dark:text-purple-300"
            >
              Tax ID (SSN / EIN)
            </Label>
            <Input
              id="exchanger-tax-id"
              aria-label="Tax ID (SSN / EIN)"
              placeholder="e.g. 123-45-6789"
              disabled={disabled}
              {...register('taxIdNumber')}
              className="border-purple-300 dark:border-purple-600 focus-visible:ring-purple-400"
            />
            {errors.taxIdNumber && (
              <p className="text-xs text-red-600 dark:text-red-400" role="alert">
                {errors.taxIdNumber.message}
              </p>
            )}
          </div>
        ) : (
          <MaskedSsnField label="Tax ID (SSN / EIN)" value={taxIdNumber} />
        )}
        {(needsAddress || needsTaxId) && !disabled && (
          <button type="submit" form="exchanger-editable-form" className="sr-only">
            Save exchanger fields
          </button>
        )}
      </form>
    </div>
  )
}

function UploadPrompt({ onGoToUpload }: { onGoToUpload: () => void }) {
  return (
    <div className="rounded-lg border border-purple-100 dark:border-purple-900/40 bg-purple-50/30 dark:bg-purple-900/10 px-4 py-3">
      <div className="flex items-start gap-3">
        <Sparkles
          className="h-4 w-4 text-purple-400 dark:text-purple-500 mt-0.5 flex-shrink-0"
          aria-hidden="true"
        />
        <div className="flex-1">
          <p className="text-sm font-semibold text-purple-900 dark:text-purple-100">
            Upload the Purchase Agreement to auto-fill
          </p>
          <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
            Neela will extract these fields — or type them manually below.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={onGoToUpload}
          className="border-purple-300 dark:border-purple-600 text-purple-700 dark:text-purple-300 hover:bg-purple-50 dark:hover:bg-purple-900/20 flex-shrink-0"
          aria-label="Go to Purchase Agreement upload step"
        >
          <FilePlus className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
          Upload
        </Button>
      </div>
    </div>
  )
}


interface SellerTabProps {
  extracted: PurchaseAgreementExtracted | null
  manualFields: ManualSellerFields
  disabled: boolean
  onGoToUpload: () => void
  onFieldsSaved: (patch: Partial<ManualSellerFields>) => void
}

const SellerTab: React.FC<SellerTabProps> = ({
  extracted,
  manualFields,
  disabled,
  onGoToUpload,
  onFieldsSaved,
}) => {
  const sellerName = extracted?.sellerName || manualFields.sellerName
  const acknowledgmentSigner = extracted?.sellerAcknowledgmentSigner || manualFields.sellerAcknowledgmentSigner
  const needsSellerName = !sellerName
  const needsSigner = !acknowledgmentSigner

  const { register, handleSubmit, formState: { errors } } = useForm<SellerEditValues>({
    resolver: zodResolver(sellerEditSchema),
    defaultValues: {
      sellerName: manualFields.sellerName || '',
      sellerAcknowledgmentSigner: manualFields.sellerAcknowledgmentSigner || '',
    },
  })

  const onSave = useCallback(
    (values: SellerEditValues) => {
      const patch: Partial<ManualSellerFields> = {}
      if (needsSellerName) patch.sellerName = values.sellerName
      if (needsSigner) patch.sellerAcknowledgmentSigner = values.sellerAcknowledgmentSigner
      onFieldsSaved(patch)
    },
    [needsSellerName, needsSigner, onFieldsSaved]
  )

  return (
    <div className="space-y-4">
      <UploadPrompt onGoToUpload={onGoToUpload} />
      <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10 px-4 py-3">
        <div className="flex items-start gap-3">
          <User
            className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          <div>
            <p className="text-sm font-semibold text-purple-900 dark:text-purple-100">
              Seller Information
            </p>
            <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
              {extracted ? 'Extracted by Neela — read-only.' : 'Enter seller details manually or upload the Purchase Agreement.'}
            </p>
          </div>
        </div>
      </div>
      <form
        id="seller-editable-form"
        onSubmit={handleSubmit(onSave)}
        noValidate
        className="space-y-3"
      >
        <EditableOrReadOnlyField
          label="Seller Name(s) / Entity"
          value={sellerName}
          fieldId="seller-name"
          placeholder="Enter seller name"
          disabled={disabled}
          registerProps={register('sellerName')}
          error={errors.sellerName?.message}
        />
        <EditableOrReadOnlyField
          label="Acknowledgment Signer"
          value={acknowledgmentSigner}
          fieldId="seller-acknowledgment-signer"
          placeholder="Enter acknowledgment signer"
          disabled={disabled}
          registerProps={register('sellerAcknowledgmentSigner')}
          error={errors.sellerAcknowledgmentSigner?.message}
        />
        {(needsSellerName || needsSigner) && !disabled && (
          <button type="submit" form="seller-editable-form" className="sr-only">
            Save seller fields
          </button>
        )}
      </form>
    </div>
  )
}

interface ClosingCompanyTabProps {
  extracted: PurchaseAgreementExtracted | null
  manualFields: ManualClosingFields
  disabled: boolean
  onGoToUpload: () => void
  onFieldsSaved: (patch: Partial<ManualClosingFields>) => void
}

const ClosingCompanyTab: React.FC<ClosingCompanyTabProps> = ({
  extracted,
  manualFields,
  disabled,
  onGoToUpload,
  onFieldsSaved,
}) => {
  const closingCompanyName = extracted?.closingCompanyName || manualFields.closingCompanyName
  const closingCompanyAddress = extracted?.closingCompanyAddress || manualFields.closingCompanyAddress
  const closingCompanyPhone = extracted?.closingCompanyPhone || manualFields.closingCompanyPhone
  const closingCompanyEmail = extracted?.closingCompanyEmail || manualFields.closingCompanyEmail
  const closerName = extracted?.closerName || manualFields.closerName
  const closerEmail = extracted?.closerEmail || manualFields.closerEmail

  const { register, handleSubmit, formState: { errors } } = useForm<ClosingEditValues>({
    resolver: zodResolver(closingEditSchema),
    defaultValues: {
      closingCompanyName: manualFields.closingCompanyName || '',
      closingCompanyAddress: manualFields.closingCompanyAddress || '',
      closingCompanyPhone: manualFields.closingCompanyPhone || '',
      closingCompanyEmail: manualFields.closingCompanyEmail || '',
      closerName: manualFields.closerName || '',
      closerEmail: manualFields.closerEmail || '',
    },
  })

  const onSave = useCallback(
    (values: ClosingEditValues) => {
      const patch: Partial<ManualClosingFields> = {}
      if (!closingCompanyName) patch.closingCompanyName = values.closingCompanyName
      if (!closingCompanyAddress) patch.closingCompanyAddress = values.closingCompanyAddress
      if (!closingCompanyPhone) patch.closingCompanyPhone = values.closingCompanyPhone
      if (!closingCompanyEmail) patch.closingCompanyEmail = values.closingCompanyEmail
      if (!closerName) patch.closerName = values.closerName
      if (!closerEmail) patch.closerEmail = values.closerEmail
      onFieldsSaved(patch)
    },
    [closingCompanyName, closingCompanyAddress, closingCompanyPhone, closingCompanyEmail, closerName, closerEmail, onFieldsSaved]
  )

  const hasAnyBlank = !closingCompanyName || !closingCompanyAddress

  return (
    <div className="space-y-4">
      <UploadPrompt onGoToUpload={onGoToUpload} />
      <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10 px-4 py-3">
        <div className="flex items-start gap-3">
          <Building2
            className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          <div>
            <p className="text-sm font-semibold text-purple-900 dark:text-purple-100">
              Closing / Title Company
            </p>
            <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
              {extracted ? 'Extracted by Neela — read-only.' : 'Enter closing company details manually or upload the Purchase Agreement.'}
            </p>
          </div>
        </div>
      </div>
      <form
        id="closing-editable-form"
        onSubmit={handleSubmit(onSave)}
        noValidate
        className="space-y-3"
      >
        <EditableOrReadOnlyField
          label="Company Name"
          value={closingCompanyName}
          fieldId="closing-company-name"
          placeholder="Enter company name"
          disabled={disabled}
          registerProps={register('closingCompanyName')}
          error={errors.closingCompanyName?.message}
        />
        <EditableOrReadOnlyField
          label="Address"
          value={closingCompanyAddress}
          fieldId="closing-company-address"
          placeholder="Enter company address"
          disabled={disabled}
          registerProps={register('closingCompanyAddress')}
          error={errors.closingCompanyAddress?.message}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <EditableOrReadOnlyField
            label="Phone"
            value={closingCompanyPhone}
            fieldId="closing-company-phone"
            placeholder="Enter phone"
            disabled={disabled}
            registerProps={register('closingCompanyPhone')}
            error={errors.closingCompanyPhone?.message}
          />
          <EditableOrReadOnlyField
            label="Company Email"
            value={closingCompanyEmail}
            fieldId="closing-company-email"
            placeholder="Enter company email"
            disabled={disabled}
            registerProps={register('closingCompanyEmail')}
            error={errors.closingCompanyEmail?.message}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <EditableOrReadOnlyField
            label="Closer Name"
            value={closerName}
            fieldId="closing-closer-name"
            placeholder="Enter closer name"
            disabled={disabled}
            registerProps={register('closerName')}
            error={errors.closerName?.message}
          />
          <EditableOrReadOnlyField
            label="Closer Email"
            value={closerEmail}
            fieldId="closing-closer-email"
            placeholder="Enter closer email"
            disabled={disabled}
            registerProps={register('closerEmail')}
            error={errors.closerEmail?.message}
          />
        </div>
        {hasAnyBlank && !disabled && (
          <button type="submit" form="closing-editable-form" className="sr-only">
            Save closing fields
          </button>
        )}
      </form>
    </div>
  )
}

interface ReplacementPropertyTabProps {
  extracted: PurchaseAgreementExtracted | null
  manualFields: ManualPropertyFields
  identifiedProperties: IdentifiedProperty[]
  finalizationStates: PropertyFinalizationState[]
  idRule: IdentificationRule | ''
  disabled: boolean
  onGoToUpload: () => void
  onFieldsSaved: (patch: Partial<ManualPropertyFields>) => void
  onFinalizationStatesChange: (states: PropertyFinalizationState[]) => void
}

const ReplacementPropertyTab: React.FC<ReplacementPropertyTabProps> = ({
  extracted,
  manualFields,
  identifiedProperties,
  finalizationStates,
  idRule,
  disabled,
  onGoToUpload,
  onFieldsSaved,
  onFinalizationStatesChange,
}) => {
  const purchaseAgreementDate = extracted?.purchaseAgreementDate || manualFields.purchaseAgreementDate
  const closingDate = extracted?.closingDate || manualFields.closingDate

  const { register, handleSubmit } = useForm<PropertyEditValues>({
    resolver: zodResolver(propertyEditSchema),
    defaultValues: {
      purchaseAgreementDate: manualFields.purchaseAgreementDate || '',
      closingDate: manualFields.closingDate || '',
    },
  })

  const onSave = useCallback(
    (values: PropertyEditValues) => {
      const patch: Partial<ManualPropertyFields> = {}
      if (!purchaseAgreementDate) patch.purchaseAgreementDate = values.purchaseAgreementDate
      if (!closingDate) patch.closingDate = values.closingDate
      onFieldsSaved(patch)
    },
    [purchaseAgreementDate, closingDate, onFieldsSaved]
  )

  const hasAnyBlank = !purchaseAgreementDate || !closingDate

  return (
    <div className="space-y-4">
      <PurchaseFinalizationList
        identifiedProperties={identifiedProperties}
        finalizationStates={finalizationStates}
        idRule={idRule}
        disabled={disabled}
        onStatesChange={onFinalizationStatesChange}
      />
      <UploadPrompt onGoToUpload={onGoToUpload} />
      <div className="rounded-lg border border-purple-200 dark:border-purple-800 bg-purple-50/50 dark:bg-purple-900/10 px-4 py-3">
        <div className="flex items-start gap-3">
          <MapPin
            className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0"
            aria-hidden="true"
          />
          <div>
            <p className="text-sm font-semibold text-purple-900 dark:text-purple-100">
              Replacement Property Details
            </p>
            <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">
              {extracted ? 'Extracted by Neela — read-only.' : 'Enter dates manually or upload the Purchase Agreement.'}
            </p>
          </div>
        </div>
      </div>
      <form
        id="property-editable-form"
        onSubmit={handleSubmit(onSave)}
        noValidate
        className="space-y-3"
      >
        <EditableOrReadOnlyField
          label="Purchase Agreement Date"
          value={purchaseAgreementDate}
          fieldId="property-purchase-agreement-date"
          placeholder="YYYY-MM-DD"
          disabled={disabled}
          registerProps={register('purchaseAgreementDate')}
        />
        <EditableOrReadOnlyField
          label="Closing Date"
          value={closingDate}
          fieldId="property-closing-date"
          placeholder="YYYY-MM-DD"
          disabled={disabled}
          registerProps={register('closingDate')}
        />
        {hasAnyBlank && !disabled && (
          <button type="submit" form="property-editable-form" className="sr-only">
            Save property fields
          </button>
        )}
      </form>
    </div>
  )
}

export const PurchaseHub: React.FC<{ onClose: () => void; onSectionComplete?: () => void }> = ({
  onClose,
  onSectionComplete,
}) => {
  const { user } = useAuth()
  const { currentExchange } = useExchange()
  const [activeStep, setActiveStep] = useState<PurchaseStep>('exchanger')
  const [tabConfirmations, setTabConfirmations] = useState<Record<string, boolean>>({
    exchanger: false,
    'purchase-agreement': false,
    seller: false,
    'closing-company': false,
    property: false,
    documents: false,
  })
  const [isSubmitted, setIsSubmitted] = useState(
    () => localStorage.getItem('purchase_hub_submitted') === 'true'
  )
  const [signingDoc, setSigningDoc] = useState<string | null>(null)
  const [viewingDoc, setViewingDoc] = useState<string | null>(null)
  const [docStatuses, setDocStatuses] = useState<Record<string, PurchaseDocState>>(
    buildInitialDocStatuses
  )
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null)

  const [purchaseAgreementPhase, setPurchaseAgreementPhase] =
    useState<PurchaseAgreementPhase>('idle')
  const [purchaseAgreementFileName, setPurchaseAgreementFileName] = useState<string>('')
  const [purchaseAgreementExtracted, setPurchaseAgreementExtracted] =
    useState<PurchaseAgreementExtracted | null>(null)

  const [manualSellerFields, setManualSellerFields] = useState<ManualSellerFields>({
    sellerName: '',
    sellerAcknowledgmentSigner: '',
  })
  const [manualClosingFields, setManualClosingFields] = useState<ManualClosingFields>({
    closingCompanyName: '',
    closingCompanyAddress: '',
    closingCompanyPhone: '',
    closingCompanyEmail: '',
    closerName: '',
    closerEmail: '',
  })
  const [manualPropertyFields, setManualPropertyFields] = useState<ManualPropertyFields>({
    purchaseAgreementDate: '',
    closingDate: '',
  })
  const [finalizationStates, setFinalizationStates] = useState<PropertyFinalizationState[]>([])

  const hudFileInputRef = useRef<HTMLInputElement>(null)
  const purchaseAgreementFileInputRef = useRef<HTMLInputElement>(null)

  const { data: acquisitionData, getData, saveData, flush, hydrated } = useHubData('acquisition')
  const { getData: getInitialData, saveData: saveInitialData } = useHubData('initial')
  const { getData: getExchangeData } = useHubData('exchange')
  const access = useHubAccess('purchase', user)
  useDemoAcquisitionPrefill(getData, saveData, saveInitialData, getExchangeData, getInitialData)

  const rawPhaseOne = getInitialData('exchangerPhaseOne') as HubData['exchangerPhaseOne']
  const rawPhaseTwo = getInitialData('exchangerPhaseTwo') as HubData['exchangerPhaseTwo']
  const rawClosingCompany = getInitialData('closingCompanyPurchase') as HubData['closingCompanyPurchase']

  const exchangerNames = rawPhaseTwo?.names || rawPhaseOne?.names || ''
  const [capturedExchangerAddress, setCapturedExchangerAddress] = useState<string>('')
  const [capturedExchangerTaxId, setCapturedExchangerTaxId] = useState<string>('')

  const exchangerHomeAddress =
    rawPhaseTwo?.homeAddress || rawPhaseOne?.homeAddress || capturedExchangerAddress
  const exchangerTaxId =
    rawPhaseTwo?.taxIdNumber || rawPhaseOne?.taxIdNumber || capturedExchangerTaxId

  const rawIdentifiedProperties = getExchangeData('identificationProperties') as
    | IdentifiedProperty[]
    | undefined
  const identifiedProperties = Array.isArray(rawIdentifiedProperties)
    ? rawIdentifiedProperties
    : []

  const rawIdentificationForm = getExchangeData('identificationForm') as HubData['identificationForm']
  const idRule = (rawIdentificationForm?.idRule as IdentificationRule | '' | undefined) ?? ''

  useEffect(() => {
    document.body.classList.add('modal-open')
    return () => {
      document.body.classList.remove('modal-open')
    }
  }, [])

  useEffect(() => {
    if (!user?.id) return
    const next: Record<string, boolean> = {}
    for (const tab of Object.keys(TAB_CONFIRM_KEYS)) {
      next[tab] = localStorage.getItem(tabConfirmStorageKey(user.id, tab)) === 'true'
    }
    setTabConfirmations(next)
  }, [user?.id])

  useEffect(() => {
    const persisted = getData('purchaseAgreementExtracted') as PurchaseAgreementExtracted | undefined
    if (persisted?.sellerName) {
      setPurchaseAgreementExtracted(persisted)
      setPurchaseAgreementPhase('revealed')
      const savedFileName = getData('purchaseAgreementFileName') as string | undefined
      if (savedFileName) setPurchaseAgreementFileName(savedFileName)
    }
  }, [getData])

  useEffect(() => {
    const persisted = getData('purchaseDocStatuses') as
      | Record<string, PurchaseDocState>
      | undefined
    if (persisted) {
      setDocStatuses(persisted)
    }
    const persistedSeller = getData('manualSellerFields') as ManualSellerFields | undefined
    if (persistedSeller) {
      setManualSellerFields(persistedSeller)
    }
    const persistedClosing = getData('manualClosingFields') as ManualClosingFields | undefined
    if (persistedClosing) {
      setManualClosingFields(persistedClosing)
    }
    const persistedProperty = getData('manualPropertyFields') as ManualPropertyFields | undefined
    if (persistedProperty) {
      setManualPropertyFields(persistedProperty)
    }
    const persistedFinalization = getData('finalizationStates') as PropertyFinalizationState[] | undefined
    if (persistedFinalization) {
      setFinalizationStates(persistedFinalization)
    }
  }, [getData])

  useEffect(() => {
    setLastSavedAt(acquisitionData?.lastSavedAt ?? null)
  }, [acquisitionData?.lastSavedAt])

  useEffect(() => {
    return () => {
      flush?.()
    }
  }, [flush])

  useEffect(() => {
    setFinalizationStates((prev) => {
      if (identifiedProperties.length === 0) return prev
      if (prev.length === identifiedProperties.length) return prev
      if (prev.length < identifiedProperties.length) {
        return [
          ...prev,
          ...buildInitialFinalizationStates(identifiedProperties.length - prev.length),
        ]
      }
      return prev.slice(0, identifiedProperties.length)
    })
  }, [identifiedProperties.length])

  useEffect(() => {
    const handler = () => {
      setIsSubmitted(true)
      activityLog.logPurchaseSubmitted()
    }
    window.addEventListener('purchaseHubCompleted', handler)
    return () => window.removeEventListener('purchaseHubCompleted', handler)
  }, [])

  useEffect(() => {
    const check = () => {
      const submitted = localStorage.getItem('purchase_hub_submitted') === 'true'
      if (submitted !== isSubmitted) setIsSubmitted(submitted)
    }
    check()
    const interval = setInterval(check, 2000)
    return () => clearInterval(interval)
  }, [isSubmitted])

  const navigateToStep = useCallback(
    (step: PurchaseStep) => {
      flush?.()
      setActiveStep(step)
    },
    [flush]
  )

  const handleTabConfirmChange = useCallback(
    (tab: string, checked: boolean) => {
      setTabConfirmations((prev) => ({ ...prev, [tab]: checked }))
      if (!user?.id) return
      if (checked) {
        localStorage.setItem(tabConfirmStorageKey(user.id, tab), 'true')
      } else {
        localStorage.removeItem(tabConfirmStorageKey(user.id, tab))
      }
    },
    [user?.id]
  )

  const persistDocStatuses = useCallback(
    (updated: Record<string, PurchaseDocState>) => {
      setDocStatuses(updated)
      saveData({ purchaseDocStatuses: updated })
    },
    [saveData]
  )

  const handleExchangerFieldsSaved = useCallback(
    (patch: { homeAddress?: string; taxIdNumber?: string }) => {
      if (patch.homeAddress) setCapturedExchangerAddress(patch.homeAddress)
      if (patch.taxIdNumber) setCapturedExchangerTaxId(patch.taxIdNumber)
      const source = rawPhaseTwo ?? rawPhaseOne
      const updated = {
        ...(source ?? {}),
        names: exchangerNames,
        homeAddress: patch.homeAddress || exchangerHomeAddress,
        taxIdNumber: patch.taxIdNumber || exchangerTaxId,
      }
      saveInitialData({ exchangerPhaseTwo: updated as HubData['exchangerPhaseTwo'] })
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    },
    [
      rawPhaseTwo,
      rawPhaseOne,
      exchangerNames,
      exchangerHomeAddress,
      exchangerTaxId,
      saveInitialData,
    ]
  )

  const handlePurchaseAgreementUpload = useCallback(
    (file: File) => {
      setPurchaseAgreementFileName(file.name)
      setPurchaseAgreementPhase('uploading')
      saveData({ purchaseAgreementFileName: file.name })
      activityLog.logDocumentUploaded('Purchase Agreement (Replacement)')
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
      setTimeout(() => {
        setPurchaseAgreementPhase('extracting')
        documentExtraction
          .simulateExtraction(file, 'purchase-agreement')
          .then((result) => {
            const extracted: PurchaseAgreementExtracted = {
              sellerName: (result.sellerName as string) || '',
              sellerAcknowledgmentSigner: (result.sellerAcknowledgmentSigner as string) || undefined,
              closingCompanyName: (result.closingCompanyName as string) || '',
              closingCompanyAddress: (result.closingCompanyAddress as string) || '',
              closingCompanyPhone: (result.closingCompanyPhone as string) || undefined,
              closingCompanyEmail: (result.closingCompanyEmail as string) || undefined,
              closerName: (result.closerName as string) || undefined,
              closerEmail: (result.closerEmail as string) || undefined,
              purchaseAgreementDate: (result.purchaseAgreementDate as string) || undefined,
              closingDate: (result.closingDate as string) || undefined,
              purchasePrice: typeof result.purchasePrice === 'number' ? result.purchasePrice : undefined,
            }
            setPurchaseAgreementExtracted(extracted)
            setPurchaseAgreementPhase('revealed')
            const sellerPayload = {
              names: extracted.sellerName,
              acknowledgmentSigner: extracted.sellerAcknowledgmentSigner,
            } as HubData['seller']
            const closingPayload = {
              name: extracted.closingCompanyName,
              address: extracted.closingCompanyAddress,
              phone: extracted.closingCompanyPhone ?? '',
              closerName: extracted.closerName ?? '',
              closerEmail: extracted.closerEmail ?? '',
              email: extracted.closingCompanyEmail,
              escrowFileNumber: undefined,
            } as unknown as HubData['closingCompanyPurchase']
            const replacementPayload = {
              addressOrLegalDescription:
                identifiedProperties[0]?.address || extracted.purchaseAgreementDate || '',
              purchaseAgreementDate: extracted.purchaseAgreementDate,
              closingDate: extracted.closingDate,
            } as unknown as HubData['replacement']
            saveData({
              purchaseAgreementExtracted: extracted,
              seller: sellerPayload,
              closingCompanyPurchase: closingPayload,
              replacement: replacementPayload,
            })
            saveInitialData({
              seller: sellerPayload,
              closingCompanyPurchase: closingPayload,
            })
            window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
            toast.success('Neela extracted Purchase Agreement data — Seller and Closing tabs are now filled.')
          })
          .catch(() => {
            setPurchaseAgreementPhase('idle')
            toast.error('Extraction failed. Please try again.')
          })
      }, 400)
    },
    [saveData, saveInitialData, identifiedProperties]
  )

  const handlePurchaseAgreementReplace = useCallback(() => {
    setPurchaseAgreementPhase('idle')
    setPurchaseAgreementFileName('')
    setPurchaseAgreementExtracted(null)
    handleTabConfirmChange('purchase-agreement', false)
    saveData({ purchaseAgreementExtracted: null, purchaseAgreementFileName: '' })
    window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    purchaseAgreementFileInputRef.current?.click()
  }, [saveData, handleTabConfirmChange])

  const handleSignComplete = useCallback(
    (docName: string, signature: string) => {
      const signedAt = new Date().toISOString()
      const signedBy =
        user?.firstName && user?.lastName
          ? `${user.firstName} ${user.lastName}`
          : 'User'
      const updated = {
        ...docStatuses,
        [docName]: { ...docStatuses[docName], signed: true, signedAt, signedBy, signature },
      }
      persistDocStatuses(updated)
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
      activityLog.logDocumentSigned(docName)
      setSigningDoc(null)
      toast.success(`${docName} signed successfully!`)
    },
    [docStatuses, persistDocStatuses, user]
  )

  const handleHudUpload = async (file: File) => {
    activityLog.logDocumentUploaded('Settlement Statement (Replacement - HUD-1)')
    const hudKey = 'Settlement Statement (Replacement - HUD-1)'
    const updated = {
      ...docStatuses,
      [hudKey]: {
        ...docStatuses[hudKey],
        uploaded: true,
        uploadedAt: new Date().toISOString(),
        fileName: file.name,
      },
    }
    persistDocStatuses(updated)
    window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    toast.success(`${file.name} uploaded successfully!`)
    try {
      await documentExtraction.simulateExtraction(file, 'closing')
      toast.success('Neela extracted settlement data from HUD-1.')
    } catch {
      toast.error('Neela extraction failed — you can continue without it.')
    }
  }

  const handleManualSellerSaved = useCallback(
    (patch: Partial<ManualSellerFields>) => {
      const updated = { ...manualSellerFields, ...patch }
      setManualSellerFields(updated)
      const sellerPayload = {
        names: updated.sellerName,
        acknowledgmentSigner: updated.sellerAcknowledgmentSigner,
      } as HubData['seller']
      saveData({ manualSellerFields: updated })
      saveInitialData({ seller: sellerPayload })
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    },
    [manualSellerFields, saveData, saveInitialData]
  )

  const handleManualClosingSaved = useCallback(
    (patch: Partial<ManualClosingFields>) => {
      const updated = { ...manualClosingFields, ...patch }
      setManualClosingFields(updated)
      const closingPayload = {
        name: updated.closingCompanyName,
        address: updated.closingCompanyAddress,
        phone: updated.closingCompanyPhone,
        closerName: updated.closerName,
        closerEmail: updated.closerEmail,
        email: updated.closingCompanyEmail,
        escrowFileNumber: undefined,
      } as unknown as HubData['closingCompanyPurchase']
      saveData({ manualClosingFields: updated })
      saveInitialData({ closingCompanyPurchase: closingPayload })
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    },
    [manualClosingFields, saveData, saveInitialData]
  )

  const handleManualPropertySaved = useCallback(
    (patch: Partial<ManualPropertyFields>) => {
      const updated = { ...manualPropertyFields, ...patch }
      setManualPropertyFields(updated)
      const replacementPayload = {
        addressOrLegalDescription:
          identifiedProperties[0]?.address || updated.purchaseAgreementDate || '',
        purchaseAgreementDate: updated.purchaseAgreementDate,
        closingDate: updated.closingDate,
      } as unknown as HubData['replacement']
      saveData({ manualPropertyFields: updated, replacement: replacementPayload })
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    },
    [manualPropertyFields, identifiedProperties, saveData]
  )

  const handleFinalizationStatesChange = useCallback(
    (states: PropertyFinalizationState[]) => {
      setFinalizationStates(states)
      saveData({ finalizationStates: states })
      window.dispatchEvent(new CustomEvent('wizardDataUpdated'))
    },
    [saveData]
  )

  const purchaseAgreementComplete = purchaseAgreementPhase === 'revealed' && !!purchaseAgreementExtracted
  const exchangerComplete = !!exchangerNames
  const sellerComplete = !!(
    purchaseAgreementExtracted?.sellerName || manualSellerFields.sellerName
  )
  const closingComplete = !!(
    (purchaseAgreementExtracted?.closingCompanyName || manualClosingFields.closingCompanyName) &&
    (purchaseAgreementExtracted?.closingCompanyAddress || manualClosingFields.closingCompanyAddress)
  )
  const finalizationReady = identifiedProperties.length > 0
    ? finalizationReadyToConfirm(identifiedProperties, finalizationStates, idRule)
    : true

  const propertyComplete = !!(
    (identifiedProperties.length > 0
      ? finalizationReady
      : (purchaseAgreementExtracted?.purchaseAgreementDate || manualPropertyFields.purchaseAgreementDate))
  )

  const resolvedClosingName =
    purchaseAgreementExtracted?.closingCompanyName || manualClosingFields.closingCompanyName
  const resolvedClosingAddress =
    purchaseAgreementExtracted?.closingCompanyAddress || manualClosingFields.closingCompanyAddress

  const closingCompanyData = resolvedClosingName
    ? {
        name: resolvedClosingName,
        address: resolvedClosingAddress,
        phone: purchaseAgreementExtracted?.closingCompanyPhone ?? manualClosingFields.closingCompanyPhone,
        closerName: purchaseAgreementExtracted?.closerName ?? manualClosingFields.closerName,
        closerEmail: purchaseAgreementExtracted?.closerEmail ?? manualClosingFields.closerEmail,
        email: purchaseAgreementExtracted?.closingCompanyEmail ?? manualClosingFields.closingCompanyEmail,
        escrowFileNumber: (rawClosingCompany as { escrowFileNumber?: string } | undefined)
          ?.escrowFileNumber,
      }
    : undefined

  const resolvedSellerName =
    purchaseAgreementExtracted?.sellerName || manualSellerFields.sellerName

  const sellerData = resolvedSellerName
    ? {
        names: resolvedSellerName,
        acknowledgmentSigner:
          purchaseAgreementExtracted?.sellerAcknowledgmentSigner ??
          manualSellerFields.sellerAcknowledgmentSigner,
      }
    : undefined

  const resolvedPurchaseAgreementDate =
    purchaseAgreementExtracted?.purchaseAgreementDate || manualPropertyFields.purchaseAgreementDate
  const resolvedClosingDate =
    purchaseAgreementExtracted?.closingDate || manualPropertyFields.closingDate

  const replacementPropertyData = resolvedPurchaseAgreementDate || resolvedClosingDate
    ? {
        addressOrLegalDescription:
          identifiedProperties[0]?.address || resolvedPurchaseAgreementDate || '',
        purchaseAgreementDate: resolvedPurchaseAgreementDate,
        closeDate: resolvedClosingDate,
      }
    : undefined

  const exchangerData = {
    names: exchangerNames,
    homeAddress: exchangerHomeAddress,
    taxIdNumber: exchangerTaxId,
    street: '',
    city: '',
    state: '',
    zipCode: '',
  }

  const exchangeAgreementDate = currentExchange?.createdAt
    ? new Date(currentExchange.createdAt).toISOString().slice(0, 10)
    : undefined
  const open1031FileNumber = currentExchange?.id
    ? `OPEN1031-${currentExchange.id.replace(/-/g, '').slice(0, 8).toUpperCase()}`
    : undefined

  const documentRenderData: DocumentRenderData = {
    exchanger: exchangerData,
    seller: sellerData,
    property: replacementPropertyData,
    closingCompany: closingCompanyData,
    bundleMeta: {
      open1031FileNumber,
      exchangeAgreementDate,
      exchangeOfficer: 'Tom Bottenberg',
      settlementAgent: closingCompanyData?.name,
      escrowFileNumber: (closingCompanyData as { escrowFileNumber?: string } | undefined)
        ?.escrowFileNumber,
      intermediaryOfficerName: 'Tom Bottenberg',
      intermediaryOfficerTitle: 'Chief Operations Officer',
    },
  }

  const { perDocument: docReadiness } = evaluateMultiple(
    ['assignment-of-purchase-contract', 'notice-of-assignment-seller'],
    documentRenderData
  )

  const allRequiredDocsDone = PURCHASE_HUB_DOCUMENTS.filter((doc) => doc.required).every((doc) => {
    const state = docStatuses[doc.name]
    if (doc.actionType === 'e-sign') return state?.signed ?? false
    if (doc.actionType === 'upload') return state?.uploaded ?? false
    if (doc.actionType === 'auto-generate') return true
    return true
  })

  const allFormTabsConfirmed =
    tabConfirmations.exchanger &&
    tabConfirmations['purchase-agreement'] &&
    tabConfirmations.seller &&
    tabConfirmations['closing-company'] &&
    tabConfirmations.property

  const allTabsConfirmed = allFormTabsConfirmed && tabConfirmations.documents

  const canSubmit = allTabsConfirmed && allRequiredDocsDone && !isSubmitted

  const handleSubmit = useCallback(() => {
    try {
      if (!allRequiredDocsDone) {
        toast.error('Please complete all required documents before submitting.')
        return
      }

      if (idRule === '95-percent') {
        const propsForValidation = identifiedProperties.map((p) => ({
          estimatedFMV: p.estimatedFMV || 0,
          allocationAmount: p.allocationAmount ?? 0,
        }))
        const closedFMVTotal = finalizationStates.reduce(
          (sum, s) => sum + (s.acquired && s.confirmed ? s.finalAmount : 0),
          0,
        )
        const closeResult = validatePurchaseClose('95-percent', propsForValidation, closedFMVTotal)
        if (!closeResult.valid) {
          toast.error(
            closeResult.blockReason || 'Purchase does not satisfy the 95% identification rule.'
          )
          return
        }
      }

      const submittedDate = new Date().toISOString()
      localStorage.setItem('purchase_hub_submitted', 'true')
      localStorage.setItem('purchase_hub_submitted_date', submittedDate)

      const exchangeId = currentExchange?.id || user?.id || ''
      if (exchangeId) {
        wizardApi
          .updateHubStatus(exchangeId, {
            hubSubmissions: {
              purchase: { submitted: true, date: submittedDate },
            },
          })
          .catch((error: unknown) => {
            console.error('Failed to sync purchase hub status to DB:', error)
          })
      }

      setIsSubmitted(true)
      activityLog.logPurchaseSubmitted()
      onSectionComplete?.()
      window.dispatchEvent(new CustomEvent('purchaseHubCompleted'))
      toast.success('Purchase Hub submitted successfully!')
    } catch {
      toast.error('Failed to submit. Please try again.')
    }
  }, [
    allRequiredDocsDone,
    idRule,
    identifiedProperties,
    finalizationStates,
    currentExchange?.id,
    user?.id,
    onSectionComplete,
  ])

  const liveCompletionPercent = (() => {
    if (isSubmitted) return 100
    const completed = [
      exchangerComplete,
      purchaseAgreementComplete,
      sellerComplete,
      closingComplete,
      propertyComplete,
      allTabsConfirmed,
      allRequiredDocsDone,
    ].filter(Boolean).length
    return Math.round((completed / 7) * 100)
  })()

  const steps: HubStep[] = [
    {
      key: 'exchanger',
      label: 'Exchanger Info',
      icon: User,
      completed: exchangerComplete && (tabConfirmations.exchanger || isSubmitted),
    },
    {
      key: 'purchase-agreement',
      label: 'Purchase Agreement',
      icon: FilePlus,
      completed: purchaseAgreementComplete && (tabConfirmations['purchase-agreement'] || isSubmitted),
    },
    {
      key: 'seller',
      label: 'Seller Info',
      icon: User,
      completed: sellerComplete && (tabConfirmations.seller || isSubmitted),
    },
    {
      key: 'closing-company',
      label: 'Closing Company',
      icon: Building2,
      completed: closingComplete && (tabConfirmations['closing-company'] || isSubmitted),
    },
    {
      key: 'property',
      label: 'Replacement Property',
      icon: MapPin,
      completed: propertyComplete && (tabConfirmations.property || isSubmitted),
    },
    {
      key: 'documents',
      label: 'Documents',
      icon: FileText,
      completed: allRequiredDocsDone && (tabConfirmations.documents || isSubmitted),
    },
  ]

  if (!access.canAccess) {
    return (
      <div className="modal-overlay">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-6 my-4">
          <div className="text-center">
            <Building2 className="h-12 w-12 text-purple-600 dark:text-purple-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
              Purchase Hub Locked
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-6">
              {access.message || 'Please complete the Identification Hub first'}
            </p>
            <Button onClick={onClose}>
              <X className="h-4 w-4 mr-1.5" />
              Close
            </Button>
          </div>
        </div>
      </div>
    )
  }

  if (!hydrated && !acquisitionData) {
    return (
      <div className="modal-overlay">
        <div className="bg-white dark:bg-slate-800 rounded-xl shadow-2xl w-full max-w-md p-8 my-4 flex flex-col items-center gap-4">
          <LoadingSpinner size="lg" variant="icon" />
          <p className="text-sm text-slate-600 dark:text-slate-400">Loading hub data…</p>
        </div>
      </div>
    )
  }

  const esignDocItems: EsignDocumentItem[] = [
    {
      key: 'assignment-of-purchase-contract',
      title: 'Assignment of Replacement Property Contract',
      description: 'Assigns the purchase contract to the QI for exchange purposes',
      signed: docStatuses['Assignment of Replacement Property Contract']?.signed ?? false,
      actionType: 'e-sign',
      onSign: () => setSigningDoc('Assignment of Replacement Property Contract'),
      onView: () => setViewingDoc('Assignment of Replacement Property Contract'),
      disabled: isSubmitted,
    },
    {
      key: 'notice-of-assignment-seller',
      title: 'Notice of Assignment (Seller)',
      description: 'Notifies the seller that the contract has been assigned to the QI',
      signed: docStatuses['Notice of Assignment (Seller)']?.signed ?? false,
      actionType: 'e-sign',
      onSign: () => setSigningDoc('Notice of Assignment (Seller)'),
      onView: () => setViewingDoc('Notice of Assignment (Seller)'),
      disabled: isSubmitted,
    },
    {
      key: 'settlement-statement-hud1',
      title: 'Settlement Statement (Replacement - HUD-1)',
      description: 'Final settlement/closing statement for the replacement property purchase',
      signed: false,
      actionType: 'upload',
      uploaded: docStatuses['Settlement Statement (Replacement - HUD-1)']?.uploaded ?? false,
      uploadedFileName: docStatuses['Settlement Statement (Replacement - HUD-1)']?.fileName,
      onUpload: () => hudFileInputRef.current?.click(),
      disabled: isSubmitted,
    },
  ]

  const esignParties: EsignParty[] = [
    {
      label: 'Documents',
      documents: esignDocItems,
    },
  ]

  const formTabKeys = Object.keys(TAB_CONFIRM_KEYS)

  return (
    <HubShell
      icon={Building2}
      title="Purchase Hub"
      subtitle="Verify → upload → extract → confirm."
      completionPercent={liveCompletionPercent}
      isSubmitted={isSubmitted}
      color="purple"
      onClose={onClose}
      canSubmit={canSubmit}
      completedMessage="Purchase Hub Completed"
      nextHubMessage="Disbursement Hub is now unlocked"
      onSubmit={handleSubmit}
      submitLabel={
        isSubmitted
          ? 'Submitted'
          : !allTabsConfirmed
            ? 'Confirm All Tabs to Submit'
            : 'Submit Hub'
      }
      lastSavedAt={lastSavedAt}
    >
      <StepTabs
        steps={steps}
        activeStep={activeStep}
        onStepChange={(key) => navigateToStep(key as PurchaseStep)}
        sectionTitle="Replacement Property Purchase"
        color="purple"
        isSubmitted={isSubmitted}
      />

      {activeStep === 'exchanger' && (
        <div className="space-y-4">
          <ExchangerTab
            names={exchangerNames}
            homeAddress={exchangerHomeAddress}
            taxIdNumber={exchangerTaxId}
            disabled={isSubmitted}
            onFieldsSaved={handleExchangerFieldsSaved}
          />
          <ConfirmationToggle
            id="confirm-purchase-tab-exchanger"
            label={TAB_CONFIRM_LABELS.exchanger}
            checked={isSubmitted ? true : (tabConfirmations.exchanger ?? false)}
            onChange={(checked) => {
              handleTabConfirmChange('exchanger', checked)
              if (checked && !isSubmitted && exchangerComplete) {
                navigateToStep('purchase-agreement')
              }
            }}
            disabled={isSubmitted || !exchangerComplete}
            visible={exchangerComplete || isSubmitted}
          />
        </div>
      )}

      {activeStep === 'purchase-agreement' && (
        <div className="space-y-4">
          <div className="rounded-xl border border-purple-200 dark:border-purple-800 bg-gradient-to-br from-purple-50/80 to-indigo-50/60 dark:from-purple-900/20 dark:to-indigo-900/10 px-4 py-3">
            <div className="flex items-start gap-3">
              <FilePlus
                className="h-4 w-4 text-purple-600 dark:text-purple-400 mt-0.5 flex-shrink-0"
                aria-hidden="true"
              />
              <div>
                <p className="text-sm font-semibold text-purple-900 dark:text-purple-100">
                  Upload your Purchase Agreement
                </p>
                <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5 leading-relaxed">
                  Neela will read the document, extract the seller, closing company, purchase
                  price, and dates — then pre-fill the next tabs read-only.
                </p>
              </div>
            </div>
          </div>

          {purchaseAgreementPhase === 'idle' && (
            <button
              type="button"
              onClick={() => purchaseAgreementFileInputRef.current?.click()}
              disabled={isSubmitted}
              aria-label="Upload purchase agreement"
              data-testid="purchase-agreement-upload-button"
              className={[
                'w-full flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-6 py-10 transition-all',
                isSubmitted
                  ? 'border-slate-200 dark:border-slate-700 opacity-60 cursor-not-allowed'
                  : 'border-purple-300 dark:border-purple-700 hover:border-purple-500 dark:hover:border-purple-500 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 cursor-pointer',
              ].join(' ')}
            >
              <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/40 flex items-center justify-center">
                <Upload
                  className="h-6 w-6 text-purple-600 dark:text-purple-400"
                  aria-hidden="true"
                />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-purple-800 dark:text-purple-200">
                  Drop your Purchase Agreement here
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400 mt-0.5">
                  PDF, JPG, or PNG · Neela reads it instantly
                </p>
              </div>
            </button>
          )}

          {purchaseAgreementPhase === 'uploading' && (
            <div
              className="rounded-xl border border-purple-200 dark:border-purple-700 bg-purple-50 dark:bg-purple-900/20 p-4 flex items-center gap-3"
              role="status"
              aria-live="polite"
            >
              <span
                className="h-5 w-5 rounded-full border-2 border-purple-500 border-t-transparent animate-spin flex-shrink-0"
                aria-hidden="true"
              />
              <p className="text-sm text-purple-800 dark:text-purple-200">
                Uploading {purchaseAgreementFileName}…
              </p>
            </div>
          )}

          {purchaseAgreementPhase === 'extracting' && (
            <PurchaseAgreementExtracting fileName={purchaseAgreementFileName} />
          )}

          {purchaseAgreementPhase === 'revealed' && purchaseAgreementExtracted && (
            <>
              <PurchaseAgreementReveal extracted={purchaseAgreementExtracted} />
              {!isSubmitted && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handlePurchaseAgreementReplace}
                    aria-label="Replace purchase agreement"
                    className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-white dark:bg-slate-700 border border-slate-200 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-600 transition-colors"
                  >
                    <RefreshCw className="h-3 w-3" aria-hidden="true" />
                    Replace document
                  </button>
                </div>
              )}
            </>
          )}

          <input
            ref={purchaseAgreementFileInputRef}
            type="file"
            accept=".pdf,.jpg,.jpeg,.png"
            className="sr-only"
            aria-label="Upload purchase agreement file"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (file) {
                handlePurchaseAgreementUpload(file)
                e.target.value = ''
              }
            }}
          />

          <ConfirmationToggle
            id="confirm-purchase-tab-purchase-agreement"
            label={TAB_CONFIRM_LABELS['purchase-agreement']}
            checked={isSubmitted ? true : (tabConfirmations['purchase-agreement'] ?? false)}
            onChange={(checked) => {
              handleTabConfirmChange('purchase-agreement', checked)
              if (checked && !isSubmitted && purchaseAgreementComplete) {
                navigateToStep('seller')
              }
            }}
            disabled={isSubmitted || !purchaseAgreementComplete}
            visible={true}
          />
        </div>
      )}

      {activeStep === 'seller' && (
        <div className="space-y-4">
          <SellerTab
            extracted={purchaseAgreementExtracted}
            manualFields={manualSellerFields}
            disabled={isSubmitted}
            onGoToUpload={() => navigateToStep('purchase-agreement')}
            onFieldsSaved={handleManualSellerSaved}
          />
          <ConfirmationToggle
            id="confirm-purchase-tab-seller"
            label={TAB_CONFIRM_LABELS.seller}
            checked={isSubmitted ? true : (tabConfirmations.seller ?? false)}
            onChange={(checked) => {
              handleTabConfirmChange('seller', checked)
              if (checked && !isSubmitted && sellerComplete) {
                navigateToStep('closing-company')
              }
            }}
            disabled={isSubmitted || !sellerComplete}
            visible={sellerComplete || isSubmitted}
          />
        </div>
      )}

      {activeStep === 'closing-company' && (
        <div className="space-y-4">
          <ClosingCompanyTab
            extracted={purchaseAgreementExtracted}
            manualFields={manualClosingFields}
            disabled={isSubmitted}
            onGoToUpload={() => navigateToStep('purchase-agreement')}
            onFieldsSaved={handleManualClosingSaved}
          />
          <ConfirmationToggle
            id="confirm-purchase-tab-closing-company"
            label={TAB_CONFIRM_LABELS['closing-company']}
            checked={isSubmitted ? true : (tabConfirmations['closing-company'] ?? false)}
            onChange={(checked) => {
              handleTabConfirmChange('closing-company', checked)
              if (checked && !isSubmitted && closingComplete) {
                navigateToStep('property')
              }
            }}
            disabled={isSubmitted || !closingComplete}
            visible={closingComplete || isSubmitted}
          />
        </div>
      )}

      {activeStep === 'property' && (
        <div className="space-y-4">
          <ReplacementPropertyTab
            extracted={purchaseAgreementExtracted}
            manualFields={manualPropertyFields}
            identifiedProperties={identifiedProperties}
            finalizationStates={
              finalizationStates.length === identifiedProperties.length
                ? finalizationStates
                : buildInitialFinalizationStates(identifiedProperties.length)
            }
            idRule={idRule}
            disabled={isSubmitted}
            onGoToUpload={() => navigateToStep('purchase-agreement')}
            onFieldsSaved={handleManualPropertySaved}
            onFinalizationStatesChange={handleFinalizationStatesChange}
          />
          <ConfirmationToggle
            id="confirm-purchase-tab-property"
            label={TAB_CONFIRM_LABELS.property}
            checked={isSubmitted ? true : (tabConfirmations.property ?? false)}
            onChange={(checked) => {
              handleTabConfirmChange('property', checked)
              if (checked && !isSubmitted && propertyComplete) {
                navigateToStep('documents')
              }
            }}
            disabled={isSubmitted || !propertyComplete}
            visible={propertyComplete || isSubmitted}
          />
        </div>
      )}

      {activeStep === 'documents' && (
        <div className="space-y-4">
          {!allFormTabsConfirmed && !isSubmitted && (
            <div className="p-4 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-3">
                <FileText
                  className="h-5 w-5 text-purple-500 mt-0.5 flex-shrink-0"
                  aria-hidden="true"
                />
                <p className="text-sm text-purple-800 dark:text-purple-200">
                  Complete all tabs before e-signing.
                </p>
              </div>
            </div>
          )}
          <DocumentReadinessPanel readiness={docReadiness} />
          <EsignDocumentsPanel
            banner={{
              title: 'Complete your Purchase Hub documents',
              subtitle: 'E-sign and upload the documents below',
            }}
            parties={esignParties}
            canSign={allFormTabsConfirmed}
            blockedReason={allFormTabsConfirmed ? undefined : 'Complete all tabs before e-signing.'}
            readOnly={isSubmitted}
            color="purple"
          />

          <input
            ref={hudFileInputRef}
            type="file"
            className="hidden"
            accept=".pdf,.doc,.docx,image/*"
            aria-label="Upload Settlement Statement HUD-1"
            onChange={async (e) => {
              const file = e.target.files?.[0]
              if (file) await handleHudUpload(file)
              e.target.value = ''
            }}
          />

          {!allRequiredDocsDone && (
            <div className="p-4 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
              <div className="flex items-start gap-3">
                <Upload className="h-5 w-5 text-amber-500 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                    Documents required to submit
                  </p>
                  <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                    Sign and upload all required documents to unlock submission.
                  </p>
                </div>
              </div>
            </div>
          )}

          <ConfirmationToggle
            id="confirm-purchase-tab-documents"
            label={TAB_CONFIRM_LABELS.documents}
            checked={isSubmitted ? true : (tabConfirmations.documents ?? false)}
            onChange={(checked) => handleTabConfirmChange('documents', checked)}
            disabled={isSubmitted || !allRequiredDocsDone}
            visible={allRequiredDocsDone || isSubmitted}
          />
        </div>
      )}

      {signingDoc && (
        <DocumentSigningModal
          documentName={signingDoc}
          documentType={DOC_TYPE_MAP[signingDoc] ?? 'assignment-of-purchase-contract'}
          exchangerData={exchangerData}
          sellerData={sellerData}
          closingCompanyData={closingCompanyData}
          propertyData={replacementPropertyData}
          bundleMeta={documentRenderData.bundleMeta}
          signerEmail={user?.email || ''}
          exchangeId={currentExchange?.id || user?.id || ''}
          onSign={(signature) => handleSignComplete(signingDoc, signature)}
          onClose={() => setSigningDoc(null)}
        />
      )}

      {viewingDoc && (
        <DocumentSigningModal
          documentName={viewingDoc}
          documentType={DOC_TYPE_MAP[viewingDoc] ?? 'assignment-of-purchase-contract'}
          exchangerData={exchangerData}
          sellerData={sellerData}
          closingCompanyData={closingCompanyData}
          propertyData={replacementPropertyData}
          bundleMeta={documentRenderData.bundleMeta}
          signerEmail={user?.email || ''}
          exchangeId={currentExchange?.id || user?.id || ''}
          onSign={() => {}}
          onClose={() => setViewingDoc(null)}
          viewOnly
        />
      )}

      {!formTabKeys.includes(activeStep) && !isSubmitted && (
        <div
          className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40 px-4 py-3"
          role="status"
          aria-label="Confirmation status"
        >
          <p className="text-xs text-slate-600 dark:text-slate-400">
            {allTabsConfirmed
              ? 'All tabs confirmed. Complete the documents above to submit.'
              : `Confirm all tabs before submitting: ${Object.entries(tabConfirmations)
                  .filter(([, confirmed]) => !confirmed)
                  .map(([tab]) => tab)
                  .join(', ')}`}
          </p>
        </div>
      )}
    </HubShell>
  )
}
